import { createContext, useContext, useId, useState } from 'react';
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { EmptyState } from './EmptyState';
import { StatusChip } from './StatusChip';
import { WS_COLOR, WS_FONT, WS_RADIUS } from './tokens';

/** How a series is drawn. Colour is never the only cue: line style and marker differ too. */
export type SeriesKind = 'active' | 'comparison' | 'forecast' | 'measured' | 'selected' | 'range';

export interface LegendItem {
  label: string;
  kind: SeriesKind;
}

export interface ChartTable {
  columns: readonly string[];
  rows: readonly (readonly (string | number)[])[];
}

/**
 * A layout that gives a chart a region of definite height (Portfolio analysis) wraps it in
 * `<ChartFit fit>`: the chart then scales to fill that region instead of taking its natural
 * height, so the legend and provenance stay in view. Elsewhere charts keep their aspect ratio.
 */
const ChartFitContext = createContext(false);
export const ChartFit = ChartFitContext.Provider;

/** What the frame shows in place of a chart when there is no chart to draw. */
export type ChartFrameState =
  | { kind: 'loading' }
  /** No data for this view (a filter, a scenario with nothing in it). Say why and what to do. */
  | { kind: 'empty'; title?: string; description?: ReactNode; action?: ReactNode }
  | { kind: 'error'; message?: ReactNode; onRetry?: () => void };

interface ChartFrameBase {
  title: string;
  /** Units of the measure, e.g. "NPV, $M". Always stated. */
  units: string;
  /** The scenario this chart is drawn for, e.g. "Base case". */
  scenario?: string;
  /** True when the data is synthetic or illustrative. Said on the chart, not in a footnote. */
  illustrative?: boolean;
}

export type ChartFrameProps = ChartFrameBase &
  (
    | {
        /** Omit when the chart is ready. */
        state?: undefined;
        legend: readonly LegendItem[];
        /** Where the numbers come from and when. */
        provenance: string;
        /** The same data as a table: the non-visual alternative. Required with a chart. */
        table: ChartTable;
        children: ReactNode;
      }
    | {
        /** The frame keeps its title and units, so the reader knows what is missing. */
        state: ChartFrameState;
        legend?: readonly LegendItem[];
        provenance?: string;
        table?: ChartTable;
        children?: ReactNode;
      }
  );

/** Draws one legend key as a small SVG, in the same style as the series it names. */
function Key({ kind }: { kind: SeriesKind }) {
  const common = { stroke: WS_COLOR.primary, strokeWidth: 2, fill: 'none' } as const;
  return (
    <svg width="28" height="12" aria-hidden focusable="false">
      {kind === 'range' ? (
        <rect
          x="1"
          y="1"
          width="26"
          height="10"
          fill={WS_COLOR.surfaceSubtle}
          stroke={WS_COLOR.border}
        />
      ) : null}
      {kind === 'forecast' ? (
        <>
          <line x1="1" x2="27" y1="6" y2="6" {...common} strokeDasharray="4 3" />
          <circle
            cx="14"
            cy="6"
            r="3"
            fill={WS_COLOR.surface}
            stroke={WS_COLOR.primary}
            strokeWidth="1.5"
          />
        </>
      ) : null}
      {kind === 'measured' || kind === 'active' ? (
        <>
          <line x1="1" x2="27" y1="6" y2="6" {...common} />
          <circle cx="14" cy="6" r="3" fill={WS_COLOR.primary} />
        </>
      ) : null}
      {kind === 'comparison' ? (
        <line
          x1="1"
          x2="27"
          y1="6"
          y2="6"
          stroke={WS_COLOR.textMuted}
          strokeWidth="2"
          strokeDasharray="1 3"
          strokeLinecap="round"
        />
      ) : null}
      {kind === 'selected' ? (
        <>
          <circle cx="14" cy="6" r="4.5" fill={WS_COLOR.primary} />
        </>
      ) : null}
    </svg>
  );
}

/**
 * A chart's frame (DESIGN.md "ChartFrame"): title, units, scenario, legend, provenance and a
 * data-table alternative — everything a reader needs to trust the picture. The chart is
 * the children; it must carry `role="img"` and an `aria-label` that states the finding.
 *
 * A chart is never an unlabelled picture: units are stated, illustrative data is marked, and
 * the table gives the same numbers to a screen reader or anyone who prefers them.
 */
export function ChartFrame(props: ChartFrameProps) {
  const { title, units, scenario, illustrative, state } = props;
  const [showTable, setShowTable] = useState(false);
  const fit = useContext(ChartFitContext);
  const uid = useId();

  if (state) {
    return (
      <Box
        component="figure"
        aria-labelledby={`${uid}-t`}
        aria-busy={state.kind === 'loading' || undefined}
        sx={{
          m: 0,
          p: 4,
          minWidth: 0,
          minHeight: 0,
          ...(fit ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}),
          backgroundColor: WS_COLOR.surface,
          border: `1px solid ${WS_COLOR.border}`,
          borderRadius: `${WS_RADIUS.card}px`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
            <Typography id={`${uid}-t`} variant="h2" component="figcaption">
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
              {units}
              {scenario ? ` · ${scenario}` : ''}
            </Typography>
          </Box>
          {illustrative ? <StatusChip tone="warning" label="Illustrative data" /> : null}
        </Box>
        {/* The same height a chart would take, so the layout does not jump when data arrives. */}
        <Box sx={{ minHeight: 220, ...(fit ? { flex: 1 } : {}) }}>
          {state.kind === 'loading' ? (
            <EmptyState kind="loading" title="Loading the chart" />
          ) : state.kind === 'empty' ? (
            <EmptyState
              kind="empty"
              title={state.title ?? 'No data for this view'}
              {...(state.description ? { description: state.description } : {})}
              {...(state.action ? { action: state.action } : {})}
            />
          ) : (
            <EmptyState
              kind="error"
              title="Couldn't load this chart"
              description={state.message ?? 'The data for this chart did not load.'}
              {...(state.onRetry
                ? {
                    action: (
                      <Button variant="tonal" onClick={state.onRetry}>
                        Try again
                      </Button>
                    ),
                  }
                : {})}
            />
          )}
        </Box>
      </Box>
    );
  }

  const { legend, provenance, table, children } = props;
  return (
    <Box
      component="figure"
      aria-labelledby={`${uid}-t`}
      sx={{
        m: 0,
        p: 4,
        minWidth: 0,
        minHeight: 0,
        ...(fit ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}),
        backgroundColor: WS_COLOR.surface,
        border: `1px solid ${WS_COLOR.border}`,
        borderRadius: `${WS_RADIUS.card}px`,
      }}
    >
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
          <Typography id={`${uid}-t`} variant="h2" component="figcaption">
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
            {units}
            {scenario ? ` · ${scenario}` : ''}
          </Typography>
        </Box>
        {illustrative ? <StatusChip tone="warning" label="Illustrative data" /> : null}
        <Button
          variant="text"
          aria-pressed={showTable}
          onClick={() => setShowTable((v) => !v)}
          sx={{ flex: 'none' }}
        >
          {showTable ? 'Show chart' : 'Show data table'}
        </Button>
      </Box>

      {showTable ? (
        <Box sx={{ overflow: 'auto', ...(fit ? { flex: 1, minHeight: 0 } : {}) }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 14,
              fontVariantNumeric: 'tabular-nums lining-nums',
              '& th, & td': { py: 1, px: 2, borderBottom: `1px solid ${WS_COLOR.border}` },
              '& th': { textAlign: 'left', fontWeight: 600, fontSize: 13 },
              '& td:not(:first-of-type), & th:not(:first-of-type)': { textAlign: 'right' },
            }}
          >
            <caption style={{ position: 'absolute', left: -9999 }}>{`${title}, ${units}`}</caption>
            <thead>
              <tr>
                {table.columns.map((c) => (
                  <th key={c} scope="col">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r, i) => (
                <tr key={i}>
                  {r.map((v, j) =>
                    j === 0 ? (
                      <th key={j} scope="row">
                        {v}
                      </th>
                    ) : (
                      <td key={j}>{v}</td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            // Fit: the chart is laid out against the region, not its own content, so nothing
            // below it (legend, provenance) is pushed past the frame.
            ...(fit
              ? {
                  flex: 1,
                  minHeight: 0,
                  position: 'relative',
                  '& > svg': { position: 'absolute', inset: 0, width: '100%', height: '100%' },
                }
              : { '& > svg': { display: 'block', width: '100%', height: 'auto' } }),
          }}
        >
          {children}
        </Box>
      )}

      <Box
        component="ul"
        aria-label="Legend"
        sx={{ listStyle: 'none', m: 0, mt: 3, p: 0, display: 'flex', gap: 5, flexWrap: 'wrap' }}
      >
        {legend.map((l) => (
          <Box
            component="li"
            key={l.label}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: 13 }}
          >
            <Key kind={l.kind} />
            {l.label}
          </Box>
        ))}
      </Box>
      <Typography variant="caption" component="div" sx={{ color: WS_COLOR.textMuted, mt: 2 }}>
        {provenance}
      </Typography>
    </Box>
  );
}

/** Shared SVG text style so every chart uses the product face and scale. */
export const CHART_TEXT = {
  fontFamily: WS_FONT.sans,
  fontSize: 12,
  fill: WS_COLOR.textMuted,
} as const;
